import React, { useRef } from "react";

import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { cn } from "@/lib/cn";

import { ClearInputValueButton } from "./ClearInputValueButton";
import { InputProps } from "./types";

export const TextInput = (props: InputProps) => {
  const {
    className,
    clearable,
    disabled = false,
    error,
    id,
    label,
    labelVisible = true,
    required,
    onBlur,
    onChange,
    value,
    ...other
  } = props;
  const ref = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur && onBlur(e.currentTarget.value);
  };

  const clear = () => {
    onChange(undefined);
    setTimeout(() => ref?.current?.focus());
  };

  return (
    <div className={cn("w-full", className)}>
      <Label htmlFor={id} variant="field" className={cn(!labelVisible && "sr-only")}>
        {label}
        {required && " *"}
      </Label>
      <div className="relative w-full">
        <Input
          {...other}
          ref={ref}
          id={id}
          type="text"
          disabled={disabled}
          value={value || ""}
          aria-invalid={!!error}
          aria-errormessage={error ? `${id}-error-message` : undefined}
          // Screen readers read aria-describedby on focus; aria-errormessage alone is widely ignored
          aria-describedby={error ? `${id}-error-message` : undefined}
          className={cn(
            "pr-9 font-semibold text-foreground transition-colors placeholder:font-normal",
            error && "border-destructive text-destructive-fg caret-destructive",
          )}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {clearable && value !== undefined && value.length > 0 && !disabled && (
          <ClearInputValueButton title="Clear" onClick={clear} />
        )}
      </div>
      {error && (
        <span
          id={`${id}-error-message`}
          className="mt-1 block w-full text-xs font-semibold text-destructive-fg"
        >
          {error}
        </span>
      )}
    </div>
  );
};
